<script setup lang="ts">
import { computed, ref, watchEffect } from 'vue'
import { SidebarLayout } from '@hantera/design-system'
import { useRegistry, useContext, useLocalization } from '@hantera/portal-app'
import ChannelList from './ChannelList.vue'
import ChannelEditor from './ChannelEditor.vue'

const context = useContext()
const t = useLocalization('settings.channels')
const registry = useRegistry()

watchEffect(() => {
  if (context.currentView.value?.name) {
    context.currentView.value.name.value = t('title')
  }
})

const canRead = computed(() => context.authorize('registry/channels:read'))

interface ChannelItem {
  key: string
  data: Record<string, any>
}

const channels = ref<ChannelItem[]>([])
const selectedKey = ref<string | null>(null)
const loading = ref(true)

const selectedChannel = computed(() => {
  if (!selectedKey.value) return null
  return channels.value.find(c => c.key === selectedKey.value) ?? null
})

async function loadChannels() {
  loading.value = true
  try {
    const data = await registry.list('channels', true)
    channels.value = Object.entries(data).map(([fullKey, value]) => ({
      key: fullKey.split('/').pop()!,
      data: typeof value === 'string' ? JSON.parse(value) : value
    })).sort((a, b) => a.key.localeCompare(b.key))
  } catch (e) {
    console.error('Failed to load channels:', e)
  } finally {
    loading.value = false
  }
}

function handleSelect(key: string | null) {
  selectedKey.value = key
}

function handleSaved() {
  loadChannels()
}

function handleDeleted() {
  selectedKey.value = null
  loadChannels()
}

function handleCreated(key: string) {
  loadChannels()
  selectedKey.value = key
}

loadChannels()
</script>

<template>
  <SidebarLayout cache-key="channels-view" v-if="canRead">
    <template #sidebar>
      <ChannelList
        :channels="channels"
        :selected-key="selectedKey"
        :loading="loading"
        @select="handleSelect"
        @created="handleCreated"
      />
    </template>
    <template #content>
      <ChannelEditor
        v-if="selectedChannel"
        :channel-key="selectedChannel.key"
        :data="selectedChannel.data"
        @saved="handleSaved"
        @deleted="handleDeleted"
      />
      <div v-else class="empty-state">
        <p>{{ t('empty') }}</p>
      </div>
    </template>
  </SidebarLayout>
</template>

<style scoped>
.empty-state {
  display: flex;
  align-items: center;
  justify-content: center;
  height: 100%;
  color: var(--color-text-secondary, hsl(var(--color-40)));
}
</style>
